import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import { TEXTS } from "./i18n/texts";

import softwareImg from "./assets/home/software.jpeg";
import designerImg from "./assets/home/designer.jpeg";

import mosImg from "./assets/team/moss.jpeg";
import neosImg from "./assets/team/neos.jpeg";
import baomImg from "./assets/team/baom.jpeg";

import iconSoftware from "./assets/icon/icon_software.svg";
import iconFrontend from "./assets/icon/icon_frontend.svg";
import iconBackend from "./assets/icon/icon_backend.svg";

import project_liftco from "./assets/projects/img_liftco.png";

// Lista de secciones que usamos en menú e indicador derecho
const SECTION_CONFIG = [
  { id: "home" },
  { id: "services" },
  { id: "projects" },
  { id: "team" },
  { id: "contact" },
];

const TEAM_IMAGES = {
  "Luis Bazalar": mosImg,
  "Renzo Alvarado": baomImg,
  "Sebastián Palomares": neosImg,
};

const SERVICE_ICONS = {
  development: iconSoftware,
  frontend: iconFrontend,
  backend: iconBackend,
};

function App() {
  // Detectar idioma del navegador
  const getBrowserLanguage = () => {
    const lang = navigator.language.slice(0, 2); // "es", "en", etc.
    return lang === "es" ? "es" : "en"; // Si no es español, usa inglés
  };

  const [lang] = useState(getBrowserLanguage());
  const t = TEXTS[lang];
  const projectItems = t?.projects?.items || [];

  const [activeProject, setActiveProject] = useState(0);
  const projectsRef = useRef(null);

  const scrollToProject = (index) => {
    const el = projectsRef.current;
    if (!el) return;

    const projects = projectItems;
    if (projects.length === 0) return;
    const clamped = Math.max(0, Math.min(index, projects.length - 1));

    const child = el.children[clamped];
    if (child) {
      child.scrollIntoView({ behavior: "smooth", inline: "center" });
    }
  };

  const handleProjectsScroll = () => {
    const el = projectsRef.current;
    if (!el) return;

    const cards = Array.from(el.children);
    const center = el.scrollLeft + el.clientWidth / 2;

    let bestIndex = 0;
    let bestDist = Infinity;

    cards.forEach((card, i) => {
      const cardCenter = card.offsetLeft + card.clientWidth / 2;
      const dist = Math.abs(cardCenter - center);
      if (dist < bestDist) {
        bestDist = dist;
        bestIndex = i;
      }
    });

    setActiveProject(bestIndex);
  };

  const [activeSection, setActiveSection] = useState("home");
  const sectionRefs = useRef({});

  // Scroll suave a sección
  const handleScrollTo = (id) => {
    const section = sectionRefs.current[id];
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Observer para saber qué sección está visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id;
          if (entry.isIntersecting) {
            setActiveSection(id);
          }
        });
      },
      { threshold: 0.6 }
    );

    SECTION_CONFIG.forEach(({ id }) => {
      const section = sectionRefs.current[id];
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="app">
      {/* NAV SUPERIOR */}
      <header className="top-nav">
        <div className="top-nav__pill">
          {SECTION_CONFIG.map((section) => (
            <button
              key={section.id}
              className={`top-nav__item ${activeSection === section.id ? "top-nav__item--active" : ""
                }`}
              onClick={() => handleScrollTo(section.id)}
            >
              {t.nav[section.id]}
            </button>
          ))}
        </div>
      </header>

      {/* INDICADOR DERECHO */}
      <div className="right-indicator">
        {SECTION_CONFIG.map((section) => (
          <button
            key={section.id}
            className={`right-indicator__dot ${activeSection === section.id
              ? "right-indicator__dot--active"
              : ""
              }`}
            onClick={() => handleScrollTo(section.id)}
            aria-label={t.nav[section.id]}
          />
        ))}
      </div>

      {/* BOTÓN CHAT FLOTANTE */}
      <button className="chat-button" aria-label="Open chat">
        💬
      </button>

      {/* SECCIÓN 1 - HERO */}
      <section
        id="home"
        ref={(el) => (sectionRefs.current["home"] = el)}
        className="section section--hero"
      >
        <div className="hero-card">
          {/* Panel Designer */}
          <div className="hero-panel hero-panel--designer">
            <img className="hero-panel__bgimg hero-panel__bgimg--designer" src={designerImg} alt="Design" />
            <div className="hero-panel__overlay hero-panel__overlay--designer" />

            <div className="hero-panel__content">
              <h2 className="hero-panel__title">{t.hero.designerTitle}</h2>
              <p className="hero-panel__subtitle">{t.hero.designerSubtitle}</p>
            </div>
          </div>

          {/* Panel Coder */}
          <div className="hero-panel hero-panel--coder">
            <img className="hero-panel__bgimg" src={softwareImg} alt="Software" />
            <div className="hero-panel__overlay" />

            <div className="hero-panel__content hero-panel__content--coder">
              <h2 className="hero-panel__title hero-panel__title--coder">
                {t.hero.coderTitle}
              </h2>
              <p className="hero-panel__subtitle">
                {t.hero.coderSubtitle}
              </p>
            </div>
          </div>
        </div>

        {/* Botones del Hero */}
        <div className="hero-actions">
          <button className="btn btn--primary">
            {t.hero.startProject}
          </button>
          <button className="btn btn--secondary">
            {t.hero.seeServices}
          </button>
        </div>

        {/* Flecha hacia abajo */}
        <button
          className="hero-scroll"
          onClick={() => handleScrollTo("services")}
        >
          ↓
        </button>
      </section>

      {/* SECCIÓN 2 - SERVICES */}
      <section
        id="services"
        ref={(el) => (sectionRefs.current["services"] = el)}
        className="section section--services"
      >
        <div className="services-wrapper">
          {/* Título y subtítulo */}
          <div className="services-header">
            <h2 className="services-title">{t.sections.services.title}</h2>
            <p className="services-subtitle">{t.sections.services.subtitle}</p>
          </div>

          {/* Tarjetas */}
          <div className="services-grid">
            {t.sections.services.items.map((item) => (
              <article key={item.key} className="service-card">
                <div className="service-card__top">
                  <div className="service-card__icon">
                    <img
                      src={SERVICE_ICONS[item.key]}
                      alt={item.title}
                      className="service-card__icon-img"
                    />
                  </div>
                  <button className="service-card__info" aria-label="More info"> i </button>
                </div>

                <h3 className="service-card__title">{item.title}</h3>
                <p className="service-card__description">{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SECCIÓN 3 - PROJECTS */}
      <section
        id="projects"
        ref={(el) => (sectionRefs.current["projects"] = el)}
        className="section section--projects"
      >
        <div className="projects-wrapper">
          <div className="projects-header">
            <h2 className="projects-title">{t.sections.projects.title}</h2>
            <p className="projects-subtitle">
              {t.sections.projects.description}
            </p>
          </div>

          <div className="projects-shell">
            {/* Flecha izquierda */}
            <button
              className="projects-arrow"
              onClick={() => scrollToProject(activeProject - 1)}
              aria-label="Previous project"
            >
              ←
            </button>

            {/* Carrusel */}
            <div
              className="projects-track"
              ref={projectsRef}
              onScroll={handleProjectsScroll}
            >
              {projectItems.map((project) => (
                <article key={project.id} className="project-card">
                  <div className="project-content">
                    <div className="project-tag">{project.tag}</div>

                    <h3 className="project-title">{project.title}</h3>

                    <div className="project-block">
                      <div className="project-label">Problem</div>
                      <p className="project-text">{project.problem}</p>
                    </div>

                    <div className="project-block">
                      <div className="project-label">Solution</div>
                      <p className="project-text">{project.solution}</p>
                    </div>

                    <div className="project-metrics">
                      {project.metrics.map((m) => (
                        <span key={m} className="project-chip">
                          {m}
                        </span>
                      ))}
                    </div>

                    <div className="project-actions">
                      <a
                        href={project.websiteUrl}
                        className="btn btn--primary"
                      >
                        {project.ctaPrimary}
                      </a>
                      <a
                        href={project.caseUrl}
                        className="btn btn--secondary"
                      >
                        {project.ctaSecondary}
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Flecha derecha */}
            <button
              className="projects-arrow"
              onClick={() => scrollToProject(activeProject + 1)}
              aria-label="Next project"
            >
              →
            </button>
          </div>

          {/* Dots */}
          <div className="projects-dots">
            {projectItems.map((_, i) => (
              <button
                key={i}
                className={`projects-dot ${i === activeProject ? "projects-dot--active" : ""
                  }`}
                onClick={() => scrollToProject(i)}
                aria-label={`Go to project ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* SECCIÓN 4 - TEAM */}
      <section
        id="team"
        ref={(el) => (sectionRefs.current["team"] = el)}
        className="section section--team"
      >
        <div className="team-wrapper">
          {/* Título y subtítulo */}
          <div className="team-header">
            <h2 className="team-heading">{t.team.heading}</h2>
            <p className="team-subtitle">{t.team.subtitle}</p>
          </div>

          {/* Tarjetas de miembros */}
          <div className="team-grid">
            {t.team.members.map((member, index) => (
              <div key={member.name} className="team-card">
                <div className={`team-avatar team-avatar--${index + 1}`}>
                  {TEAM_IMAGES[member.name] ? (
                    <img
                      src={TEAM_IMAGES[member.name]}
                      alt={member.name}
                      className="team-avatar__img"
                    />
                  ) : (
                    <span className="team-avatar__initials">
                      {member.name
                        .split(" ")
                        .map((w) => w[0])
                        .join("")
                        .slice(0, 2)}
                    </span>
                  )}
                </div>

                <h3 className="team-name">{member.name}</h3>
                <p className="team-role">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECCIÓN 5 - CONTACT */}
      <section
        id="contact"
        ref={(el) => (sectionRefs.current["contact"] = el)}
        className="section section--contact"
      >
        <div className="contact-wrapper">
          <div className="contact-header">
            <h2 className="contact-title">{t.contact.title}</h2>
            <p className="contact-subtitle">{t.contact.subtitle}</p>
          </div>

          <div className="contact-card">
            <form className="contact-form" onSubmit={(e) => e.preventDefault()}>
              <div className="contact-row">
                <div className="contact-field">
                  <label className="contact-label">{t.contact.form.nameLabel}</label>
                  <input
                    className="contact-input"
                    type="text"
                    placeholder={t.contact.form.namePlaceholder}
                  />
                </div>

                <div className="contact-field">
                  <label className="contact-label">{t.contact.form.emailLabel}</label>
                  <input
                    className="contact-input"
                    type="email"
                    placeholder={t.contact.form.emailPlaceholder}
                  />
                </div>
              </div>

              <div className="contact-field">
                <label className="contact-label">{t.contact.form.companyLabel}</label>
                <input
                  className="contact-input"
                  type="text"
                  placeholder={t.contact.form.companyPlaceholder}
                />
              </div>

              <div className="contact-field">
                <label className="contact-label">{t.contact.form.messageLabel}</label>
                <textarea
                  className="contact-textarea"
                  rows="5"
                  placeholder={t.contact.form.messagePlaceholder}
                />
              </div>

              <div className="contact-footer">
                <button className="contact-submit" type="submit">
                  {t.contact.form.sendButton} <span className="contact-arrow">→</span>
                </button>

                <div className="contact-note">
                  <div>
                    {t.contact.form.preferEmail}{" "}
                    <a className="contact-email" href={`mailto:${t.contact.form.email}`}>
                      {t.contact.form.email}
                    </a>
                  </div>
                  <div className="contact-muted">{t.contact.form.responseTime}</div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

    </div>
  );
}

export default App;
